import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { CATEGORIES_ANALYSES } from '@/components/feature/AnalyseChecklist';

interface PatientHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
}

interface TensionValues {
  bras_gauche?: string;
  bras_droit?: string;
  debout?: string;
  assis?: string;
  coucher?: string;
}

interface MedicamentEnCours {
  id: string;
  nom: string;
  dosage?: string;
}

interface MedicamentPrescrit {
  id: string;
  nom: string;
  matin?: boolean;
  midi?: boolean;
  soir?: boolean;
  dosageNb?: string;
  nbBoites?: string;
  conditionnement?: 'boite' | 'plaquette';
  posologie?: string;
}

interface SoinLigne {
  id: string;
  date: string;
  description: string;
}

interface DonneesSupplementaires {
  tensionValues?: TensionValues;
  medicamentsEnCours?: MedicamentEnCours[];
  medicamentsPrescrits?: MedicamentPrescrit[];
  selectedAnalyses?: string[];
  customAnalyses?: { id: string; label: string }[];
  patientRegulier?: boolean;
  bonLaboObs?: string;
  bonLaboUrgence?: boolean;
  dureeTraitement?: string;
  instructionsPrescription?: string;
  traitementNotes?: string;
  dossierNotes?: string;
  typeSuivi?: string;
  suiviDepartement?: string;
  showBonLabo?: boolean;
  showFicheSoin?: boolean;
  soins?: SoinLigne[];
}

interface SessionSoin {
  id: string;
  date: string;
  description: string;
  suite_soin_id: string;
  medecin_id: string | null;
  created_at: string;
  medecins?: { nom: string; prenom: string } | null;
}

interface PlainteSuivi {
  id: string;
  suite_soin_id: string;
  description: string;
  symptomes: string | null;
  date_plainte: string;
  medecin_id: string | null;
  created_at: string;
  medecins?: { nom: string; prenom: string } | null;
}

interface CompteRenduSuite {
  id: string;
  suite_soin_id: string;
  medecin_id: string | null;
  contenu: string | null;
  etat_patient: string | null;
  mesures_prises: string | null;
  instructions_suivant: string | null;
  created_at: string;
  medecins?: { nom: string; prenom: string } | null;
}

interface SuiteSoins {
  id: string;
  patient_id: string;
  consultation_id: string;
  medecin_initiateur_id: string | null;
  statut: string;
  created_at: string;
  sessions: SessionSoin[];
  plaintes: PlainteSuivi[];
  comptesRendus: CompteRenduSuite[];
}

interface TimelineItemConsultation {
  type: 'consultation';
  id: string;
  date: string;
  data: ConsultationData;
}

interface TimelineItemSession {
  type: 'session_soin';
  id: string;
  date: string;
  data: SessionSoin;
  suiteId: string;
  consultationId: string;
}

type TimelineItem = TimelineItemConsultation | TimelineItemSession;

interface ConsultationData {
  id: string;
  date_consultation: string;
  heure_debut: string;
  heure_fin: string;
  poids: number | null;
  taille: number | null;
  temperature: number | null;
  tension: string | null;
  saturation: number | null;
  symptomes_signes: string | null;
  traitement_en_cours: string | null;
  patient_regulier_medicaments: boolean | null;
  antecedents_medicaux_connus: string | null;
  examen: string | null;
  diagnostic: string | null;
  prochain_rdv: string | null;
  suivi_vers: string | null;
  mot_fin: string | null;
  statut: string;
  donnees_supplementaires: DonneesSupplementaires | null;
  heure_pause: string | null;
  heure_reprise: string | null;
  medecins?: { nom: string; prenom: string; specialite: string } | null;
  sage_femmes?: { nom: string; prenom: string } | null;
}

interface PatientData {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  maladies_chroniques: string;
  antecedents_medicaux: string;
  antecedents_chirurgicaux: string;
  numero_dossier: string;
}

const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors text-sm bg-white";
const textareaClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors resize-none text-sm bg-white";
const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

export default function PatientHistoryModal({
  isOpen,
  onClose,
  patientId,
  patientName,
}: PatientHistoryModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [consultations, setConsultations] = useState<ConsultationData[]>([]);
  const [suitesSoins, setSuitesSoins] = useState<SuiteSoins[]>([]);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<TimelineItem | null>(null);
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const mountedRef = useRef(true);
  const [suiteActionLoading, setSuiteActionLoading] = useState(false);
  const [suiteActionMsg, setSuiteActionMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!isOpen || !patientId) return;
    mountedRef.current = true;
    setSelectedItem(null); // Reset on open
    loadAllData();
    return () => {
      mountedRef.current = false;
    };
  }, [isOpen, patientId]);

  const loadAllData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      await Promise.all([loadPatient(), loadConsultations(), loadSuitesSoins()]);
    } catch (err: any) {
      const msg = err?.message || String(err);
      console.error('Erreur chargement historique:', err);
      if (mountedRef.current) {
        setLoadError(msg);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const loadPatient = async () => {
    const { data } = await supabase
      .from('patients')
      .select('*')
      .eq('id', patientId)
      .single();
    if (data && mountedRef.current) setPatient(data);
  };

  const loadSuitesSoins = async () => {
    const { data: suitesData, error } = await supabase
      .from('suites_soins')
      .select('id, patient_id, consultation_id, medecin_initiateur_id, statut, created_at')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });

    if (error || !suitesData) {
      console.error('Erreur chargement suites soins:', error);
      return;
    }
    if (!mountedRef.current) return;

    const suiteIds = suitesData.map((s: any) => s.id);
    let sessionsMap: Record<string, SessionSoin[]> = {};
    let plaintesMap: Record<string, PlainteSuivi[]> = {};
    let crMap: Record<string, CompteRenduSuite[]> = {};
    if (suiteIds.length > 0) {
      const [{ data: sessionsData }, { data: plaintesData }, { data: crData }] = await Promise.all([
        supabase
          .from('sessions_soins')
          .select('id, suite_soin_id, description, date_soin, medecin_id, created_at, medecins(nom, prenom)')
          .in('suite_soin_id', suiteIds)
          .order('date_soin', { ascending: false }),
        supabase
          .from('plaintes_suivi')
          .select('id, suite_soin_id, description, symptomes, date_plainte, medecin_id, created_at, medecins(nom, prenom)')
          .in('suite_soin_id', suiteIds)
          .order('date_plainte', { ascending: false }),
        supabase
          .from('comptes_rendus_suite')
          .select('id, suite_soin_id, medecin_id, contenu, etat_patient, mesures_prises, instructions_suivant, created_at, medecins(nom, prenom)')
          .in('suite_soin_id', suiteIds)
          .order('created_at', { ascending: false }),
      ]);

      sessionsMap = (sessionsData || []).reduce((acc: Record<string, SessionSoin[]>, sess: any) => {
        if (!acc[sess.suite_soin_id]) acc[sess.suite_soin_id] = [];
        acc[sess.suite_soin_id].push({
          id: sess.id,
          suite_soin_id: sess.suite_soin_id,
          date: sess.date_soin,
          description: sess.description,
          medecin_id: sess.medecin_id,
          created_at: sess.created_at,
          medecins: sess.medecins || null,
        });
        return acc;
      }, {});

      plaintesMap = (plaintesData || []).reduce((acc: Record<string, PlainteSuivi[]>, p: any) => {
        if (!acc[p.suite_soin_id]) acc[p.suite_soin_id] = [];
        acc[p.suite_soin_id].push(p);
        return acc;
      }, {});

      crMap = (crData || []).reduce((acc: Record<string, CompteRenduSuite[]>, cr: any) => {
        if (!acc[cr.suite_soin_id]) acc[cr.suite_soin_id] = [];
        acc[cr.suite_soin_id].push(cr);
        return acc;
      }, {});
    }

    const suites: SuiteSoins[] = suitesData.map((s: any) => ({
      ...s,
      sessions: sessionsMap[s.id] || [],
      plaintes: plaintesMap[s.id] || [],
      comptesRendus: crMap[s.id] || [],
    }));

    setSuitesSoins(suites);
  };

  const loadConsultations = async () => {
    const { data, error } = await supabase
      .from('consultations')
      .select(`
        *,
        medecins (nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_consultation', { ascending: false })
      .order('heure_debut', { ascending: false })
      .limit(50);

    if (error) throw error;
    if (!mountedRef.current) return;

    const sageFemmeIds = (data || [])
      .filter((c: any) => !c.medecins && c.medecin_id)
      .map((c: any) => c.medecin_id);

    let sageFemmesMap: Record<string, { nom: string; prenom: string }> = {};
    if (sageFemmeIds.length > 0) {
      const { data: sfData } = await supabase
        .from('sage_femmes')
        .select('id, nom, prenom')
        .in('id', sageFemmeIds);
      if (sfData) {
        sfData.forEach((sf: any) => {
          sageFemmesMap[sf.id] = { nom: sf.nom, prenom: sf.prenom };
        });
      }
    }

    const enriched = (data || []).map((c: any) => ({
      ...c,
      sage_femmes: (!c.medecins && c.medecin_id && sageFemmesMap[c.medecin_id])
        ? sageFemmesMap[c.medecin_id]
        : null,
    }));

    setConsultations(enriched);
    // Build timeline after both consultations and suites are loaded
    // This will be called after loadSuitesSoins too via useEffect
  };

  // Build unified timeline whenever consultations or suites change
  useEffect(() => {
    if (consultations.length === 0 && suitesSoins.length === 0) {
      setTimelineItems([]);
      setSelectedItem(null);
      return;
    }

    const items: TimelineItem[] = [];

    // Add consultations
    consultations.forEach((cons) => {
      items.push({
        type: 'consultation',
        id: cons.id,
        date: cons.date_consultation,
        data: cons,
      });
    });

    // Add all sessions from all suites
    suitesSoins.forEach((suite) => {
      suite.sessions.forEach((sess) => {
        items.push({
          type: 'session_soin',
          id: sess.id,
          date: sess.date || sess.created_at,
          data: sess,
          suiteId: suite.id,
          consultationId: suite.consultation_id,
        });
      });
    });

    // Sort by date descending, then by created_at for same-day items (consultations before sessions if same day)
    items.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateB.getTime() !== dateA.getTime()) {
        return dateB.getTime() - dateA.getTime();
      }
      // Same day: consultations first, then sessions by created_at desc
      if (a.type === 'consultation' && b.type !== 'consultation') return -1;
      if (b.type === 'consultation' && a.type !== 'consultation') return 1;
      const createdA = a.type === 'session_soin' ? new Date(a.data.created_at).getTime() : new Date(a.data.heure_debut || a.data.date_consultation).getTime();
      const createdB = b.type === 'session_soin' ? new Date(b.data.created_at).getTime() : new Date(b.data.heure_debut || b.data.date_consultation).getTime();
      return createdB - createdA;
    });

    setTimelineItems(items);
    // Auto-select first on desktop
    if (!isMobile && items.length > 0 && !selectedItem) {
      setSelectedItem(items[0]);
    }
  }, [consultations, suitesSoins, isMobile]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateShort = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '--:--';
    return timeStr.substring(0, 5);
  };

  const statutBadge = (s: string) => {
    switch (s) {
      case 'termine': return { text: 'Terminé', cls: 'bg-green-100 text-green-700 border-green-200' };
      case 'partiellement_termine': return { text: 'Pause labo', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'en_cours': return { text: 'En cours', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
      default: return { text: s, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
    }
  };

  const hasTensionDetail = (dd: DonneesSupplementaires | null) => {
    if (!dd?.tensionValues) return false;
    const tv = dd.tensionValues;
    return !!(tv.bras_gauche || tv.bras_droit || tv.debout || tv.assis || tv.coucher);
  };

  const renderTensionBadge = (label: string, value: string | undefined) => {
    if (!value) return null;
    return (
      <div className="flex items-center gap-1.5 bg-red-50 px-2.5 py-1 rounded-lg border border-red-100">
        <span className="text-xs text-red-600 font-medium">{label}</span>
        <span className="text-sm font-bold text-red-800">{value}</span>
      </div>
    );
  };

  const hasMedicamentsEnCours = (dd: DonneesSupplementaires | null) => {
    return dd?.medicamentsEnCours?.some((m) => m.nom?.trim()) || false;
  };

  const hasMedicamentsPrescrits = (dd: DonneesSupplementaires | null) => {
    return dd?.medicamentsPrescrits?.some((m) => m.nom?.trim()) || false;
  };

  const resolveAnalysisLabel = (id: string, dd: DonneesSupplementaires | null) => {
    if (id.startsWith('custom-')) {
      const custom = dd?.customAnalyses?.find((a) => a.id === id);
      return custom?.label || id;
    }
    const item = CATEGORIES_ANALYSES.flatMap((c) => c.items).find((i) => i.id === id);
    return item?.label || id;
  };

  const getSuiteSoinsForConsultation = (consultationId: string) => {
    return suitesSoins.find((s) => s.consultation_id === consultationId);
  };

  const getAllSessionsForConsultation = (consultationId: string): SessionSoin[] => {
    return suitesSoins
      .filter((s) => s.consultation_id === consultationId)
      .flatMap((s) => s.sessions);
  };

  const getAllPlaintesForConsultation = (consultationId: string): PlainteSuivi[] => {
    return suitesSoins
      .filter((s) => s.consultation_id === consultationId)
      .flatMap((s) => s.plaintes);
  };

  const getAllComptesRendusForConsultation = (consultationId: string): CompteRenduSuite[] => {
    return suitesSoins
      .filter((s) => s.consultation_id === consultationId)
      .flatMap((s) => s.comptesRendus);
  };

  const handleOpenSuiteSoins = async (consultation?: ConsultationData | null) => {
    setSuiteActionLoading(true);
    setSuiteActionMsg(null);
    try {
      let medId: string | null = null;
      if (user) {
        const { data: med } = await supabase
          .from('medecins')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        medId = med?.id || null;
      }

      const targetConsultation = consultation || consultations[0] || null;

      // 1. S'il existe une suite de soins « en cours » liée à la consultation ciblée,
      //    on l'ouvre directement pour pouvoir la continuer.
      if (targetConsultation) {
        const suiteEnCoursLiee = suitesSoins.find(
          (s) => s.consultation_id === targetConsultation.id && s.statut === 'en_cours'
        );
        if (suiteEnCoursLiee) {
          navigate(`/medecin/suite-soins?suiteId=${suiteEnCoursLiee.id}`);
          return;
        }
      }

      // 2. Sinon, chercher n'importe quelle suite en cours pour ce patient
      const suiteEnCoursGlobale = suitesSoins.find((s) => s.statut === 'en_cours');
      if (suiteEnCoursGlobale) {
        navigate(`/medecin/suite-soins?suiteId=${suiteEnCoursGlobale.id}`);
        return;
      }

      // 3. Sinon, créer une nouvelle suite de soins.
      if (!targetConsultation) {
        setSuiteActionMsg({ type: 'error', msg: 'Impossible d\'ouvrir une suite de soins : le patient n\'a encore aucune consultation.' });
        return;
      }

      const { data: suite, error } = await supabase
        .from('suites_soins')
        .insert({
          patient_id: patientId,
          consultation_id: targetConsultation.id,
          medecin_initiateur_id: medId,
          statut: 'en_cours',
        })
        .select()
        .single();
      if (error) throw error;
      navigate(`/medecin/suite-soins?suiteId=${suite.id}`);
    } catch (e: any) {
      setSuiteActionMsg({ type: 'error', msg: e.message || 'Erreur lors de l\'ouverture de la suite de soins.' });
    } finally {
      setSuiteActionLoading(false);
    }
  };

  if (!isOpen) return null;

  const latestTerminated = consultations.find((cons) => cons.statut === 'termine') || null;
  const hasEnCoursSuite = suitesSoins.some((s) => s.statut === 'en_cours');
  const item = selectedItem;
  const isConsultationSelected = item?.type === 'consultation';
  const isSessionSelected = item?.type === 'session_soin';
  const c = isConsultationSelected ? item.data : null;
  const sess = isSessionSelected ? item.data : null;
  const selectedSuite = isSessionSelected ? suitesSoins.find((s) => s.id === item.suiteId) : null;
  const dd = c?.donnees_supplementaires;
  const age = patient
    ? new Date().getFullYear() - new Date(patient.date_naissance).getFullYear()
    : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Full-screen modal */}
      <div className="relative bg-white w-full h-full flex flex-col overflow-hidden mx-0 md:mx-4 md:my-4 md:rounded-3xl md:h-[calc(100%-32px)] shadow-2xl">

        {/* ===================== HEADER ===================== */}
        <div className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Dossier médical — {patientName}
              </h2>
              {patient && (
                <p className="text-sm text-gray-500">
                  {age} ans • {patient.sexe === 'M' ? 'Masculin' : patient.sexe === 'F' ? 'Féminin' : 'N/R'} • Dossier {patient.numero_dossier || 'N/A'} • {patient.groupe_sanguin || 'GS inconnu'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => {
                let targetCons: ConsultationData | null = null;
                if (selectedItem?.type === 'consultation') {
                  targetCons = selectedItem.data;
                } else if (selectedItem?.type === 'session_soin') {
                  targetCons = consultations.find((cc) => cc.id === selectedItem.consultationId) || null;
                }
                handleOpenSuiteSoins(targetCons || latestTerminated || consultations[0] || null);
              }}
              disabled={suiteActionLoading || consultations.length === 0}
              title={consultations.length === 0 ? 'Une consultation est nécessaire pour ouvrir une suite de soins' : 'Ouvrir la suite de soins'}
              className="px-3 py-1.5 bg-amber-600 text-white rounded-full text-xs font-semibold hover:bg-amber-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {suiteActionLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-empathize-line"></i>}
              {suiteActionLoading ? 'Ouverture...' : hasEnCoursSuite ? 'Continuer la suite de soins' : 'Suite de soins'}
            </button>
            <div className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">
              {consultations.length} consultation{consultations.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {suiteActionMsg && (
          <div className={`px-4 md:px-8 py-2 text-sm flex items-center gap-2 border-b ${suiteActionMsg.type === 'error' ? 'bg-red-50 text-red-700 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
            <i className={suiteActionMsg.type === 'error' ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'}></i>
            {suiteActionMsg.msg}
          </div>
        )}

        {/* ===================== BODY: Two panels ===================== */}
        <div className="flex-1 flex overflow-hidden">
          {/* ---- LEFT PANEL: Dates list ---- */}
          {/* On mobile when any item is selected, hide the left panel */}
          <div className={`${isMobile && selectedItem ? 'hidden' : 'flex'} w-full md:w-[21.25rem] lg:w-[23.75rem] border-r border-gray-200 flex-col shrink-0 bg-gray-50/50`}>
            <div className="px-4 py-3 border-b border-gray-200 bg-white">
              <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                <i className="ri-calendar-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                Historique
              </h3>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <i className="ri-loader-4-line text-2xl text-teal-500 animate-spin"></i>
              </div>
            ) : loadError ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <i className="ri-wifi-off-line text-4xl text-red-300 mb-3"></i>
                <p className="text-sm font-semibold text-gray-700">Impossible de charger l'historique</p>
                <p className="text-xs text-gray-500 mt-1 max-w-[16.25rem]">
                  Une erreur de connexion est survenue. Vérifiez votre connexion internet puis réessayez.
                </p>
                <button
                  onClick={loadAllData}
                  className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-refresh-line"></i>
                  Réessayer
                </button>
              </div>
            ) : consultations.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8">
                <i className="ri-file-history-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-sm text-gray-500 text-center">Aucune consultation enregistrée</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto py-2">
                {timelineItems.map((item) => {
                  if (item.type === 'consultation') {
                    const cons = item.data;
                    const st = statutBadge(cons.statut);
                    const isSelected = selectedItem?.type === 'consultation' && selectedItem.id === cons.id;
                    const ddCons = cons.donnees_supplementaires;

                    return (
                      <button
                        key={`cons-${cons.id}`}
                        onClick={() => setSelectedItem(item)}
                        className={`w-full text-left px-4 py-3 transition-all cursor-pointer border-l-[3px] ${
                          isSelected
                            ? 'bg-teal-50 border-l-teal-500'
                            : 'border-l-transparent hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-teal-800' : 'text-gray-800'}`}>
                              {formatDateShort(cons.date_consultation)}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">
                                {formatTime(cons.heure_debut)} - {formatTime(cons.heure_fin)}
                              </span>
                              {cons.heure_pause && (
                                <span className="text-min bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                                  Pause
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className={`text-min px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>
                                {st.text}
                              </span>
                              {getSuiteSoinsForConsultation(cons.id) && (
                                <span className="text-min px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                  Suite de soins
                                </span>
                              )}
                              {cons.medecins && (
                                <span className="text-min text-gray-500 truncate">
                                  Dr {cons.medecins.nom} {cons.medecins.prenom}
                                </span>
                              )}
                              {cons.sage_femmes && (
                                <span className="text-min text-gray-500 truncate">
                                  SF {cons.sage_femmes.nom}
                                </span>
                              )}
                            </div>
                            {/* Mini preview */}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ddCons?.tensionValues?.bras_gauche && (
                                <span className="text-min bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">
                                  {ddCons.tensionValues.bras_gauche}
                                </span>
                              )}
                              {cons.poids && (
                                <span className="text-min bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                  {cons.poids}kg
                                </span>
                              )}
                              {cons.diagnostic && (
                                <span className="text-min text-gray-400 truncate max-w-[12.5rem]">
                                  {cons.diagnostic.substring(0, 40)}{cons.diagnostic.length > 40 ? '...' : ''}
                                </span>
                              )}
                            </div>
                          </div>
                          <i className={`${isSelected ? 'ri-arrow-right-s-fill text-teal-500' : 'ri-arrow-right-s-line text-gray-300'} text-lg flex-shrink-0 mt-1`}></i>
                        </div>
                      </button>
                    );
                  }

                  // Session de soins
                  const session = item.data;
                  const isSelected = selectedItem?.type === 'session_soin' && selectedItem.id === session.id;
                  const suite = suitesSoins.find((s) => s.id === item.suiteId);
                  const parentCons = consultations.find((cc) => cc.id === item.consultationId);

                  return (
                    <button
                      key={`sess-${session.id}`}
                      onClick={() => setSelectedItem(item)}
                      className={`w-full text-left px-4 py-3 transition-all cursor-pointer border-l-[3px] ${
                        isSelected
                          ? 'bg-amber-50 border-l-amber-500'
                          : 'border-l-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-semibold truncate ${isSelected ? 'text-amber-800' : 'text-gray-800'}`}>
                            <span className="inline-flex items-center gap-1.5">
                              <i className="ri-nurse-line text-amber-500 w-3 h-3 flex items-center justify-center"></i>
                              Séance de soins
                            </span>
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">
                              {session.date ? formatDateShort(session.date) : 'Date non définie'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {session.medecins ? (
                              <span className="text-min text-gray-500 truncate">
                                Dr {session.medecins.nom} {session.medecins.prenom}
                              </span>
                            ) : parentCons?.medecins ? (
                              <span className="text-min text-gray-500 truncate">
                                Dr {parentCons.medecins.nom} {parentCons.medecins.prenom}
                              </span>
                            ) : null}
                          </div>
                          {session.description && (
                            <p className="text-min text-gray-500 truncate max-w-[12.5rem] mt-1">
                              {session.description.substring(0, 50)}{session.description.length > 50 ? '...' : ''}
                            </p>
                          )}
                        </div>
                        <i className={`${isSelected ? 'ri-arrow-right-s-fill text-amber-500' : 'ri-arrow-right-s-line text-gray-300'} text-lg flex-shrink-0 mt-1`}></i>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---- RIGHT PANEL: Full consultation detail (read-only) ---- */}
          {/* --- DESKTOP --- */}
          <div className={`${isMobile ? 'hidden' : 'flex'} flex-1 flex-col overflow-hidden bg-gray-50`}>
            {!item ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                <i className="ri-arrow-left-line text-5xl mb-3"></i>
                <p className="text-lg font-medium">Sélectionnez un élément</p>
                <p className="text-sm mt-1">Cliquez sur une consultation ou une séance de soins dans la liste de gauche</p>
              </div>
            ) : isSessionSelected ? (
              // ===== SESSION DE SOINS DETAIL =====
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
                  {/* ===== HEADER SESSION ===== */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <i className="ri-nurse-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
                        Séance de soins
                      </h2>
                      <p className="text-sm text-gray-500">
                        {sess?.date ? formatDate(sess.date) : 'Date non définie'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${
                        selectedSuite?.statut === 'terminee' ? 'bg-green-100 text-green-700 border-green-200' :
                        selectedSuite?.statut === 'en_cours' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {selectedSuite?.statut === 'terminee' ? 'Terminée' :
                         selectedSuite?.statut === 'en_cours' ? 'En cours' : 'Suite'}
                      </span>
                    </div>
                  </div>

                  {/* Parent consultation link */}
                  {(() => {
                    const parentCons = consultations.find((cc) => cc.id === item.consultationId);
                    if (!parentCons) return null;
                    return (
                      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="p-5 border-b border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-link text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                            Consultation associée
                          </h3>
                        </div>
                        <div className="p-5">
                          <button
                            onClick={() => setSelectedItem({ type: 'consultation', id: parentCons.id, date: parentCons.date_consultation, data: parentCons })}
                            className="w-full text-left flex items-center justify-between bg-teal-50 rounded-xl p-4 border border-teal-100 hover:bg-teal-100 transition-colors cursor-pointer"
                          >
                            <div>
                              <p className="text-sm font-semibold text-teal-900">
                                Consultation du {formatDate(parentCons.date_consultation)}
                              </p>
                              <p className="text-xs text-teal-700 mt-0.5">
                                {formatTime(parentCons.heure_debut)} - {formatTime(parentCons.heure_fin)}
                                {parentCons.medecins && ` · Dr ${parentCons.medecins.nom} ${parentCons.medecins.prenom}`}
                              </p>
                            </div>
                            <i className="ri-arrow-right-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Description */}
                  {sess?.description && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-file-list-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Description du soin
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{sess.description}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Effectué par */}
                  {sess?.medecins && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-user-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Effectué par
                        </h3>
                      </div>
                      <div className="p-5">
                        <p className="text-sm text-gray-800">Dr {sess.medecins.nom} {sess.medecins.prenom}</p>
                      </div>
                    </div>
                  )}

                  {/* Suite info */}
                  {(() => {
                    const suite = suitesSoins.find((s) => s.id === item.suiteId);
                    if (!suite) return null;
                    return (
                      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="p-5 border-b border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-information-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                            Informations de la suite
                          </h3>
                        </div>
                        <div className="p-5 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Statut de la suite :</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                              suite.statut === 'en_cours' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                              suite.statut === 'terminee' ? 'bg-green-100 text-green-700 border-green-200' :
                              'bg-gray-100 text-gray-700 border-gray-200'
                            }`}>
                              {suite.statut === 'en_cours' ? 'En cours' :
                               suite.statut === 'terminee' ? 'Terminée' : suite.statut}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Sessions dans cette suite :</span>
                            <span className="text-sm font-semibold text-gray-800">{suite.sessions.length}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Créée le :</span>
                            <span className="text-sm font-semibold text-gray-800">{new Date(suite.created_at).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* SPACER */}
                  <div className="h-12"></div>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">

                  {/* ===== HEADER CONSULTATION ===== */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Consultation du {formatDate(c.date_consultation)}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {formatTime(c.heure_debut)} - {formatTime(c.heure_fin || null)}
                        {c.medecins && (
                          <span className="ml-2">par Dr {c.medecins.nom} {c.medecins.prenom}</span>
                        )}
                        {c.sage_femmes && (
                          <span className="ml-2">par SF {c.sage_femmes.nom} {c.sage_femmes.prenom}</span>
                        )}
                      </p>
                      {c.heure_pause && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            Pause {formatTime(c.heure_pause)}
                          </span>
                          {c.heure_reprise && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Reprise {formatTime(c.heure_reprise)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleOpenSuiteSoins(c)}
                        disabled={suiteActionLoading}
                        className="px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {suiteActionLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-empathize-line"></i>}
                        {suiteActionLoading ? 'Ouverture...' : hasEnCoursSuite ? 'Continuer la suite de soins' : 'Suite de soins'}
                      </button>
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${statutBadge(c.statut).cls}`}>
                        {statutBadge(c.statut).text}
                      </span>
                    </div>
                  </div>

                  {/* ===== DOSSIER GÉNÉRAL ===== */}
                  {patient && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-folder-user-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Dossier Général
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Nom</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.nom}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Prénom</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.prenom}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Date de naissance</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.date_naissance ? new Date(patient.date_naissance).toLocaleDateString('fr-FR') : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.telephone || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Email</p>
                            <p className="text-sm font-semibold text-gray-900 truncate">{patient.email || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Groupe sanguin</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.groupe_sanguin || '-'}</p>
                          </div>
                          <div className="md:col-span-3">
                            <p className="text-xs text-gray-500 mb-0.5">Adresse</p>
                            <p className="text-sm font-semibold text-gray-900">{patient.adresse || '-'}</p>
                          </div>
                          <div className="md:col-span-3">
                            <p className="text-xs text-gray-500 mb-0.5">Allergies connues</p>
                            <p className="text-sm text-red-600 font-semibold">{patient.allergies || 'Aucune'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== CONSTANTES ===== */}
                  {(c.poids || c.taille || c.temperature || c.saturation || c.tension || hasTensionDetail(dd)) && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-stethoscope-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Constantes
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                          {c.poids !== null && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">Poids</div>
                              <div className="font-bold text-gray-900 text-sm">{c.poids} kg</div>
                            </div>
                          )}
                          {c.taille !== null && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">Taille</div>
                              <div className="font-bold text-gray-900 text-sm">{c.taille} cm</div>
                            </div>
                          )}
                          {c.temperature !== null && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">Température</div>
                              <div className="font-bold text-gray-900 text-sm">{c.temperature}°C</div>
                            </div>
                          )}
                          {c.saturation !== null && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">SpO₂</div>
                              <div className="font-bold text-gray-900 text-sm">{c.saturation}%</div>
                            </div>
                          )}
                          {c.tension && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                              <div className="text-xs text-gray-500 mb-0.5">Tension résumé</div>
                              <div className="font-bold text-gray-900 text-sm">{c.tension}</div>
                            </div>
                          )}
                        </div>

                        {/* Tensions détaillées */}
                        {hasTensionDetail(dd) && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">Tensions détaillées</p>
                            <div className="flex flex-wrap gap-2">
                              {renderTensionBadge('Bras gauche', dd?.tensionValues?.bras_gauche)}
                              {renderTensionBadge('Bras droit', dd?.tensionValues?.bras_droit)}
                              {renderTensionBadge('Debout', dd?.tensionValues?.debout)}
                              {renderTensionBadge('Assis', dd?.tensionValues?.assis)}
                              {renderTensionBadge('Couché', dd?.tensionValues?.coucher)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ===== SYMPTÔMES ===== */}
                  {c.symptomes_signes && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-emotion-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Symptômes & Signes
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          {c.symptomes_signes.split('\n').map((line, i) => (
                            <p key={i} className="text-sm text-gray-800 mb-1 last:mb-0">
                              {line.trim() ? `${i + 1}. ${line}` : ''}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== EXAMEN ===== */}
                  {c.examen && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-search-eye-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Examen clinique
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.examen}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== TRAITEMENT EN COURS ===== */}
                  {(hasMedicamentsEnCours(dd) || dd?.traitementNotes || c.patient_regulier_medicaments) && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-medicine-bottle-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Traitement en cours
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {hasMedicamentsEnCours(dd) && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">Médicaments</p>
                            <div className="flex flex-wrap gap-2">
                              {dd?.medicamentsEnCours?.filter((m) => m.nom?.trim()).map((m) => (
                                <span key={m.id} className="bg-amber-50 text-amber-800 px-3 py-1.5 rounded-lg text-sm font-medium border border-amber-100">
                                  {m.nom}{m.dosage ? ` - ${m.dosage}` : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {c.patient_regulier_medicaments && (
                          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                            <i className="ri-checkbox-circle-fill text-amber-500 w-4 h-4 flex items-center justify-center"></i>
                            <span className="text-sm text-amber-800">Patient régulier dans la prise de ses médicaments</span>
                          </div>
                        )}

                        {dd?.traitementNotes && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Notes</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 border border-gray-100">{dd.traitementNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ===== ANTÉCÉDENTS ===== */}
                  {c.antecedents_medicaux_connus && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-health-book-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Antécédents médicaux
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.antecedents_medicaux_connus}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== DIAGNOSTIC ===== */}
                  {c.diagnostic && (
                    <div className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden">
                      <div className="p-5 border-b border-red-100 bg-red-50/30">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-mental-health-line text-red-500 w-4 h-4 flex items-center justify-center"></i>
                          Diagnostic
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                          <p className="text-sm text-red-900 font-medium whitespace-pre-wrap">{c.diagnostic}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== BILAN (Pause/Reprise) ===== */}
                  {(c.heure_pause || c.heure_reprise) && (
                    <div className="bg-white rounded-2xl border-2 border-amber-200 overflow-hidden">
                      <div className="p-5 border-b border-amber-100 bg-amber-50/30">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-flask-line text-amber-500 w-4 h-4 flex items-center justify-center"></i>
                          Bilan
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {/* Phase 1 */}
                        <div className="flex items-start gap-4">
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-teal-100 border-2 border-teal-500 flex items-center justify-center flex-shrink-0">
                              <span className="text-teal-700 font-bold text-sm">1</span>
                            </div>
                            {c.heure_reprise && <div className="w-0.5 h-8 bg-teal-300"></div>}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-teal-700">Phase 1 — Avant bilan</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {formatDate(c.date_consultation)} · {formatTime(c.heure_debut)}
                              {c.heure_pause && ` → Pause : ${formatTime(c.heure_pause)}`}
                            </p>
                          </div>
                        </div>

                        {/* Phase 2 */}
                        {c.heure_reprise && (
                          <div className="flex items-start gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-8 h-8 rounded-full bg-amber-100 border-2 border-amber-500 flex items-center justify-center flex-shrink-0">
                                <span className="text-amber-700 font-bold text-sm">2</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-bold text-amber-700">Phase 2 — Après bilan</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Reprise : {formatTime(c.heure_reprise)}
                                {c.heure_fin && ` → Fin : ${formatTime(c.heure_fin)}`}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ===== ANALYSES (Bon Labo) ===== */}
                  {(dd?.selectedAnalyses?.length || dd?.customAnalyses?.length) ? (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-microscope-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Bon de Laboratoire
                        </h3>
                      </div>
                      <div className="p-5 space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2">Analyses demandées</p>
                          <div className="flex flex-wrap gap-2">
                            {dd?.selectedAnalyses?.map((a, i) => (
                              <span key={`sel-${i}`} className="bg-purple-50 text-purple-800 px-3 py-1.5 rounded-lg text-sm border border-purple-100">
                                {resolveAnalysisLabel(a, dd)}
                              </span>
                            ))}
                          </div>
                        </div>
                        {dd?.bonLaboUrgence && (
                          <span className="inline-block bg-red-50 text-red-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-red-100">
                            <i className="ri-alert-fill mr-1"></i>Urgent
                          </span>
                        )}
                        {dd?.bonLaboObs && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Observations cliniques</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 border border-gray-100">{dd.bonLaboObs}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* ===== PRESCRIPTION ===== */}
                  {hasMedicamentsPrescrits(dd) && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-file-text-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Prescription
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2">Médicaments prescrits</p>
                          <div className="space-y-2">
                            {dd?.medicamentsPrescrits?.filter((m) => m.nom?.trim()).map((m) => (
                              <div key={m.id} className="bg-green-50 rounded-xl p-3 border border-green-100">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                  <span className="font-semibold text-green-900 text-sm">{m.nom}</span>
                                  <div className="flex gap-1.5">
                                    {m.matin && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Matin</span>}
                                    {m.midi && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Midi</span>}
                                    {m.soir && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">Soir</span>}
                                  </div>
                                </div>
                                <p className="text-xs text-green-700 mt-1">
                                  Dosage : {m.dosageNb || '1'}/jour
                                  {m.nbBoites && m.nbBoites !== '1'
                                    ? ` · ${m.nbBoites} ${m.conditionnement === 'plaquette' ? 'plaquettes' : 'boîtes'}`
                                    : ` · 1 ${m.conditionnement === 'plaquette' ? 'plaquette' : 'boîte'}`}
                                  {m.posologie ? ` · ${m.posologie}` : ''}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {dd?.dureeTraitement && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Durée du traitement</p>
                            <p className="text-sm text-gray-800">{dd.dureeTraitement}</p>
                          </div>
                        )}

                        {dd?.instructionsPrescription && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Instructions</p>
                            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 border border-gray-100">{dd.instructionsPrescription}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ===== SOINS ===== */}
                  {dd?.soins?.some((s) => s.description?.trim()) && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-nurse-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Fiche de Soin
                        </h3>
                      </div>
                      <div className="p-5">
                        <div className="space-y-2">
                          {dd.soins?.filter((s) => s.description?.trim()).map((s) => (
                            <div key={s.id} className="flex items-start gap-3 bg-blue-50 rounded-xl p-3 border border-blue-100">
                              <div className="bg-blue-100 rounded-lg px-2.5 py-1 text-xs font-semibold text-blue-700 whitespace-nowrap">
                                {s.date ? new Date(s.date).toLocaleDateString('fr-FR') : 'Sans date'}
                              </div>
                              <p className="text-sm text-blue-900">{s.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===== SUIVI & PROCHAIN RDV ===== */}
                  {(c.suivi_vers || c.prochain_rdv) && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-arrow-right-circle-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Suivi
                        </h3>
                      </div>
                      <div className="p-5 space-y-2">
                        {c.suivi_vers && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Suivi vers :</span>
                            <span className="text-sm font-semibold text-gray-800">{c.suivi_vers}</span>
                          </div>
                        )}
                        {c.prochain_rdv && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Prochain RDV :</span>
                            <span className="text-sm font-semibold text-gray-800">{new Date(c.prochain_rdv).toLocaleDateString('fr-FR')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ===== MOT DE FIN ===== */}
                  {c.mot_fin && (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                      <div className="p-5 border-b border-gray-100">
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                          <i className="ri-chat-quote-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                          Mot de fin
                        </h3>
                      </div>
                      <div className="p-5">
                        <p className="text-sm text-gray-700 italic bg-gray-50 rounded-xl p-4 border border-gray-100">{c.mot_fin}</p>
                      </div>
                    </div>
                  )}

                  {/* ===== SUITE DE SOINS ===== */}
                  {(() => {
                    const allSessions = getAllSessionsForConsultation(c.id);
                    const allPlaintes = getAllPlaintesForConsultation(c.id);
                    const allCR = getAllComptesRendusForConsultation(c.id);
                    if (allSessions.length === 0 && allPlaintes.length === 0 && allCR.length === 0) return null;
                    return (
                      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                        <div className="p-5 border-b border-gray-100">
                          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <i className="ri-empathize-line text-amber-600 w-4 h-4 flex items-center justify-center"></i>
                            Suite de soins
                          </h3>
                        </div>
                        <div className="p-5 space-y-6">
                          {allPlaintes.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                                <i className="ri-alert-line text-amber-500 w-4 h-4 flex items-center justify-center"></i>
                                Plaintes
                              </p>
                              <div className="space-y-2">
                                {allPlaintes.map((p) => (
                                  <div key={p.id} className="flex items-start gap-3 bg-amber-50 rounded-xl p-3 border border-amber-100">
                                    <div className="flex-1 min-w-0">
                                      {p.date_plainte && (
                                        <p className="text-xs text-gray-500 mb-0.5">
                                          {new Date(p.date_plainte).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                        </p>
                                      )}
                                      <p className="text-sm text-amber-900 font-medium">{p.description}</p>
                                      {p.symptomes && <p className="text-xs text-gray-600 mt-1">Symptômes : {p.symptomes}</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {allSessions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                                <i className="ri-nurse-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                                Fiches de soin
                              </p>
                              <div className="space-y-2">
                                {allSessions.map((sess, idx) => (
                                  <div key={sess.id} className="flex items-start gap-3 bg-teal-50 rounded-xl p-3 border border-teal-100">
                                    <div className="bg-teal-100 rounded-lg px-2.5 py-1 text-xs font-semibold text-teal-700 whitespace-nowrap">
                                      #{idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      {sess.date && (
                                        <p className="text-xs text-gray-500 mb-0.5">
                                          {new Date(sess.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                        </p>
                                      )}
                                      <p className="text-sm text-teal-900 font-medium">{sess.description}</p>
                                      {sess.medecins && (
                                        <p className="text-xs text-gray-500 mt-1">Effectué par : Dr {sess.medecins.nom} {sess.medecins.prenom}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {allCR.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                                <i className="ri-file-text-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                                Comptes rendus de relève
                              </p>
                              <div className="space-y-2">
                                {allCR.map((cr) => (
                                  <div key={cr.id} className="bg-gray-50 rounded-xl p-3 border border-gray-200 space-y-2">
                                    {cr.contenu && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-600">Compte rendu</p>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.contenu}</p>
                                      </div>
                                    )}
                                    {cr.etat_patient && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-600">État du patient</p>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.etat_patient}</p>
                                      </div>
                                    )}
                                    {cr.mesures_prises && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-600">Mesures prises</p>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.mesures_prises}</p>
                                      </div>
                                    )}
                                    {cr.instructions_suivant && (
                                      <div>
                                        <p className="text-xs font-semibold text-gray-600">Instructions suivantes</p>
                                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.instructions_suivant}</p>
                                      </div>
                                    )}
                                    {cr.medecins && (
                                      <p className="text-xs text-gray-500">Rédigé par Dr {cr.medecins.nom} {cr.medecins.prenom}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ===== SPACER ===== */}
                  <div className="h-12"></div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ===== MOBILE: Detail overlay ===== */}
        {isMobile && selectedItem && (
          <div className="fixed inset-0 z-[110] bg-white overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
              <button
                onClick={() => setSelectedItem(null)}
                className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <span className="text-sm font-bold text-gray-700">
                {isConsultationSelected ? formatDateShort(c.date_consultation) : 'Séance de soins'}
              </span>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            <div className="px-4 py-5 space-y-5">
              {isSessionSelected && sess ? (
                // ===== MOBILE SESSION DETAIL =====
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <i className="ri-nurse-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
                      Séance de soins
                    </h2>
                    <p className="text-sm text-gray-500">
                      {sess.date ? formatDate(sess.date) : 'Date non définie'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                        selectedSuite?.statut === 'terminee' ? 'bg-green-100 text-green-700 border-green-200' :
                        selectedSuite?.statut === 'en_cours' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {selectedSuite?.statut === 'terminee' ? 'Terminée' :
                         selectedSuite?.statut === 'en_cours' ? 'En cours' : 'Suite'}
                      </span>
                    </div>
                  </div>

                  {(() => {
                    const parentCons = consultations.find((cc) => cc.id === (selectedItem as TimelineItemSession).consultationId);
                    if (!parentCons) return null;
                    return (
                      <button
                        onClick={() => setSelectedItem({ type: 'consultation', id: parentCons.id, date: parentCons.date_consultation, data: parentCons })}
                        className="w-full text-left flex items-center justify-between bg-teal-50 rounded-xl p-3 border border-teal-100 hover:bg-teal-100 transition-colors cursor-pointer"
                      >
                        <div>
                          <p className="text-sm font-semibold text-teal-900">Consultation associée</p>
                          <p className="text-xs text-teal-700">{formatDate(parentCons.date_consultation)} · Dr {parentCons.medecins?.nom} {parentCons.medecins?.prenom}</p>
                        </div>
                        <i className="ri-arrow-right-line text-teal-600 w-4 h-4 flex items-center justify-center"></i>
                      </button>
                    );
                  })()}

                  {sess.description && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Description</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-xl p-3 border border-gray-100">{sess.description}</p>
                    </div>
                  )}

                  {sess.medecins && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Effectué par :</span>
                      <span className="text-sm font-semibold text-gray-800">Dr {sess.medecins.nom} {sess.medecins.prenom}</span>
                    </div>
                  )}
                </>
              ) : c ? (
                // ===== MOBILE CONSULTATION DETAIL =====
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Consultation du {formatDate(c.date_consultation)}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {formatTime(c.heure_debut)} - {formatTime(c.heure_fin || null)}
                      {c.medecins && <span className="ml-2">par Dr {c.medecins.nom} {c.medecins.prenom}</span>}
                      {c.sage_femmes && <span className="ml-2">par SF {c.sage_femmes.nom} {c.sage_femmes.prenom}</span>}
                    </p>
                    {c.heure_pause && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          Pause {formatTime(c.heure_pause)}
                        </span>
                        {c.heure_reprise && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            Reprise {formatTime(c.heure_reprise)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {c.diagnostic && (
                    <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                      <p className="text-sm font-bold text-red-700 mb-1">Diagnostic</p>
                      <p className="text-sm text-red-900 font-medium">{c.diagnostic}</p>
                    </div>
                  )}

                  {(c.poids || c.taille || c.temperature || c.saturation) && (
                    <div className="grid grid-cols-2 gap-2">
                      {c.poids !== null && <div className="bg-gray-50 rounded-xl p-3"><span className="text-xs text-gray-500">Poids</span><p className="font-bold text-sm">{c.poids} kg</p></div>}
                      {c.taille !== null && <div className="bg-gray-50 rounded-xl p-3"><span className="text-xs text-gray-500">Taille</span><p className="font-bold text-sm">{c.taille} cm</p></div>}
                      {c.temperature !== null && <div className="bg-gray-50 rounded-xl p-3"><span className="text-xs text-gray-500">Temp.</span><p className="font-bold text-sm">{c.temperature}°C</p></div>}
                      {c.saturation !== null && <div className="bg-gray-50 rounded-xl p-3"><span className="text-xs text-gray-500">SpO₂</span><p className="font-bold text-sm">{c.saturation}%</p></div>}
                    </div>
                  )}

                  {c.symptomes_signes && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Symptômes & Signes</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-xl p-3 border border-gray-100">{c.symptomes_signes}</p>
                    </div>
                  )}

                  {c.examen && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Examen clinique</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-xl p-3 border border-gray-100">{c.examen}</p>
                    </div>
                  )}

                  {hasMedicamentsEnCours(dd) && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Traitement en cours</p>
                      <div className="flex flex-wrap gap-1.5">
                        {dd?.medicamentsEnCours?.filter((m) => m.nom?.trim()).map((m) => (
                          <span key={m.id} className="bg-amber-50 text-amber-800 px-2.5 py-1 rounded-lg text-xs font-medium border border-amber-100">{m.nom}{m.dosage ? ` ${m.dosage}` : ''}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasMedicamentsPrescrits(dd) && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Prescription</p>
                      <div className="space-y-1.5">
                        {dd?.medicamentsPrescrits?.filter((m) => m.nom?.trim()).map((m) => (
                          <div key={m.id} className="flex items-center justify-between bg-green-50 rounded-lg p-2.5 border border-green-100">
                            <span className="text-sm font-semibold text-green-900">{m.nom}</span>
                            <div className="flex gap-1">
                              {m.matin && <span className="text-min bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full">M</span>}
                              {m.midi && <span className="text-min bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full">Mi</span>}
                              {m.soir && <span className="text-min bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full">S</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(dd?.selectedAnalyses?.length || dd?.customAnalyses?.length) ? (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-2">Analyses demandées</p>
                      <div className="flex flex-wrap gap-1.5">
                        {dd?.selectedAnalyses?.map((a, i) => (
                          <span key={`mob-sel-${i}`} className="bg-purple-50 text-purple-800 px-2.5 py-1 rounded-lg text-xs border border-purple-100">{resolveAnalysisLabel(a, dd)}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {c.suivi_vers && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Suivi vers :</span>
                      <span className="text-sm font-semibold text-gray-800">{c.suivi_vers}</span>
                    </div>
                  )}

                  {c.prochain_rdv && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Prochain RDV :</span>
                      <span className="text-sm font-semibold text-gray-800">{new Date(c.prochain_rdv).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}

                  {c.mot_fin && (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Mot de fin</p>
                      <p className="text-sm text-gray-700 italic bg-gray-50 rounded-xl p-3 border border-gray-100">{c.mot_fin}</p>
                    </div>
                  )}

                  {/* ===== MOBILE: Suite de soins ===== */}
                  {(() => {
                    const allSessions = getAllSessionsForConsultation(c.id);
                    const allPlaintes = getAllPlaintesForConsultation(c.id);
                    const allCR = getAllComptesRendusForConsultation(c.id);
                    if (allSessions.length === 0 && allPlaintes.length === 0 && allCR.length === 0) return null;
                    return (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">Suite de soins</p>
                        {allPlaintes.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {allPlaintes.map((p) => (
                              <div key={p.id} className="flex items-start gap-2 bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                                <div className="flex-1 min-w-0">
                                  {p.date_plainte && (
                                    <p className="text-min text-gray-500">{new Date(p.date_plainte).toLocaleDateString('fr-FR')}</p>
                                  )}
                                  <p className="text-sm text-amber-900 font-medium">{p.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {allSessions.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {allSessions.map((s, idx) => (
                              <div key={s.id} className="flex items-start gap-2 bg-teal-50 rounded-lg p-2.5 border border-teal-100">
                                <span className="text-min bg-teal-200 text-teal-800 px-1.5 py-0.5 rounded font-bold shrink-0">#{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                  {s.date && (
                                    <p className="text-min text-gray-500">{new Date(s.date).toLocaleDateString('fr-FR')}</p>
                                  )}
                                  <p className="text-sm text-teal-900 font-medium">{s.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {allCR.length > 0 && (
                          <div className="space-y-2">
                            {allCR.map((cr) => (
                              <div key={cr.id} className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                                {cr.contenu && (
                                  <div>
                                    <p className="text-min font-semibold text-gray-600">Compte rendu</p>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.contenu}</p>
                                  </div>
                                )}
                                {cr.etat_patient && (
                                  <div>
                                    <p className="text-min font-semibold text-gray-600">État du patient</p>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.etat_patient}</p>
                                  </div>
                                )}
                                {cr.mesures_prises && (
                                  <div className="mt-1">
                                    <p className="text-min font-semibold text-gray-600">Mesures prises</p>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.mesures_prises}</p>
                                  </div>
                                )}
                                {cr.instructions_suivant && (
                                  <div className="mt-1">
                                    <p className="text-min font-semibold text-gray-600">Instructions suivantes</p>
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{cr.instructions_suivant}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              ) : null}

              <div className="h-8"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}